import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { homeDecisionsQuery } from '../lib/agency-home-api.js';
import {
  type Brand,
  type FeedCard,
  markNotificationRead,
  type NotificationItem,
} from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { brandsQuery, feedQuery } from '../lib/queries.js';
import { BrandChip } from './brand-chip.js';
import { Drawer, DrawerCloseButton, useDrawer } from './drawer.js';
import { SlotApprovalDrawer } from './slot-approval-drawer.js';

// Global notification drawer (s216-54h.70).
//
// Clicking ANY notification — in the bell dropdown or the /notifications
// inbox — opens a drawer instead of navigating away. Which drawer
// depends on what the notification is backed by (NotificationDrawerRouter):
//
//   - slot_approval_pending feed card → the existing SlotApprovalDrawer,
//     so the founder reviews the full slot (media + caption) and can
//     approve / reject / request-changes right there. These are the
//     ask.approval notifications with no cta — their action surface is
//     the slot drawer, not a generic body.
//   - everything else → the generic NotificationDrawer: full title +
//     body with brand / kind / time context, the approval ask if any,
//     and a CTA into the notification's destination.
//
// Mounted once at the root (NotificationDrawerProvider in __root.tsx)
// so it works from every page: the bell lives in the top nav on every
// authenticated route, and the inbox is its own page. A React context —
// not a URL search param — drives it, because the trigger surfaces are
// cross-brand + global and already hold the full NotificationItem.
//
// Chrome mirrors FeedCardDrawer / SlotDrawer: state-driven slide-in,
// plum left-edge shadow, scrim + ESC close, body scroll lock. Footer
// follows the brand button rule (primary on the right).

interface NotificationDrawerContextValue {
  /** Open the drawer for a notification. Replaces the previous
   *  navigate-on-click behavior at every call site. */
  openNotification: (notification: NotificationItem) => void;
  closeNotification: () => void;
}

const NotificationDrawerContext = createContext<NotificationDrawerContextValue | null>(null);

/** Access the global notification drawer. Must be used under
 *  NotificationDrawerProvider (mounted in __root.tsx). */
export function useNotificationDrawer(): NotificationDrawerContextValue {
  const ctx = useContext(NotificationDrawerContext);
  if (!ctx) {
    throw new Error('useNotificationDrawer must be used within NotificationDrawerProvider');
  }
  return ctx;
}

export function NotificationDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<NotificationItem | null>(null);
  // Stable context value — openNotification / closeNotification never
  // change identity, so consumers (the bell, the inbox) don't re-render
  // just because the open notification changed.
  const value = useMemo<NotificationDrawerContextValue>(
    () => ({
      openNotification: (notification) => setOpen(notification),
      closeNotification: () => setOpen(null),
    }),
    [],
  );
  return (
    <NotificationDrawerContext.Provider value={value}>
      {children}
      {open ? (
        <NotificationDrawerRouter
          // Key on id so switching directly from one notification to
          // another (without a close in between) remounts the slide-in
          // + re-runs the mark-read effect for the new row.
          key={open.id}
          notification={open}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </NotificationDrawerContext.Provider>
  );
}

/** Picks the right drawer for a notification + owns marking it read.
 *  Slot-approval notifications (ask.approval, no cta, backed by a
 *  slot_approval_pending feed card) open the full SlotApprovalDrawer;
 *  everything else gets the generic details drawer. */
function NotificationDrawerRouter({
  notification,
  onClose,
}: {
  notification: NotificationItem;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  // Mark the notification read on open — here in the router so it fires
  // regardless of which drawer renders below. Idempotent server-side.
  useEffect(() => {
    if (notification.readAt !== null) return;
    void markNotificationRead(notification.id, true)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      })
      .catch((err) => {
        console.warn('[notification-drawer] mark-read failed', err);
      });
  }, [notification.id, notification.readAt, queryClient]);

  const meta = readPayloadMeta(notification);
  const feedCardId = notification.feedCardId ?? meta.feedCardId;
  const brandId = notification.brandId;
  // Only resolve a backing card when this looks like a slot-approval:
  // no cta + a feedCardId + a brand. Slot approvals never ship a cta,
  // so cta-bearing notifications skip the feed fetch and open the
  // generic drawer instantly.
  const wantsSlot = !notification.cta && Boolean(feedCardId) && Boolean(brandId);

  const decisionsQ = useQuery({ ...homeDecisionsQuery(brandId ?? ''), enabled: wantsSlot });
  const feedQ = useQuery({ ...feedQuery(brandId ?? ''), enabled: wantsSlot });
  const card: FeedCard | null = feedCardId
    ? (decisionsQ.data?.cards.find((c) => c.id === feedCardId) ??
      feedQ.data?.cards.find((c) => c.id === feedCardId) ??
      null)
    : null;

  if (wantsSlot && brandId && card?.kind === 'slot_approval_pending') {
    return <SlotApprovalDrawer card={card} brandId={brandId} onClose={onClose} />;
  }

  // Card not resolved yet — show a loading shell rather than flashing
  // the generic drawer and swapping to the slot drawer a beat later.
  // (Warm cache resolves synchronously, so this only shows on a cold
  // cross-brand open.)
  if (wantsSlot && !card && (decisionsQ.isLoading || feedQ.isLoading)) {
    return <NotificationDrawerLoading onClose={onClose} />;
  }

  return <NotificationDrawer notification={notification} onClose={onClose} />;
}

function NotificationDrawer({
  notification,
  onClose,
}: {
  notification: NotificationItem;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { visible, requestClose } = useDrawer(onClose);

  // brandsQuery is warm on every page that renders the bell (TopNav's
  // BrandSwitcher seeds it), so the chip hydrates with no extra fetch.
  const { data: brands = [] } = useQuery(brandsQuery());
  const brand = useMemo<Brand | null>(() => {
    if (!notification.brandId) return null;
    return brands.find((b) => b.id === notification.brandId) ?? null;
  }, [brands, notification.brandId]);

  // Per-kind payload reading. The richest detail lives in payload, not
  // the flat columns: ask.approval carries `action` (what mag wants
  // approved) and most kinds carry `feedCardId` there even when the
  // top-level column is null (dispatchNotification stores it in payload;
  // only dispatchProposalNotification lifts it to the column). Reading
  // both is what lets a slot-approval notification — which has no
  // top-level cta and no top-level feedCardId — still route to its
  // backing feed card where the real approve/reject UI lives.
  const meta = useMemo(() => readPayloadMeta(notification), [notification]);
  const feedCardId = notification.feedCardId ?? meta.feedCardId;

  // Where "act on this" goes.
  //
  // 1. An explicit cta wins — with ?card=<id> injected so kinds whose
  //    backing card is FeedCardDrawer-compatible (proposals, policy
  //    issues, etc.) open that drawer on landing. These notifications
  //    ship a cta + a top-level feedCardId via dispatchProposalNotification.
  // 2. No cta but feed-card-backed (the slot-approval / bare
  //    dispatchNotification path, feedCardId in payload) → route to
  //    that brand's /home PLAIN. Those cards (slot_approval_pending)
  //    aren't FeedCardDrawer cards — they live in the polaroid stack on
  //    /home with their own approve/reject drawer, so ?card= would land
  //    a misleading dismiss-only view. Plain /home drops the founder on
  //    the surface that actually carries the action.
  //
  // Either way we target the notification's OWN brand, not the active
  // one — the card only exists in that brand's surfaces.
  const destination = useMemo<{ href: string; label: string } | null>(() => {
    if (notification.cta?.href) {
      return {
        href: withFeedCardParam(notification.cta.href, feedCardId),
        label: notification.cta.label,
      };
    }
    if (feedCardId && brand) {
      return { href: `/brand/${brand.slug}/home`, label: 'review it' };
    }
    return null;
  }, [notification.cta, feedCardId, brand]);

  // CTA → slide out, then navigate to the destination. Relative hrefs
  // route in-app (feed-card-backed notifications land on the
  // FeedCardDrawer); absolute urls open a new tab. requestClose plays
  // the slide-out + clears the open notification; we fire the nav on the
  // same beat so the route changes as the drawer leaves.
  const handleCta = () => {
    if (!destination) return;
    const { href } = destination;
    if (href.startsWith('/')) {
      requestClose();
      window.setTimeout(() => void navigate({ to: href as never }), 260);
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const created = new Date(notification.createdAt);
  const priorityBadge = notification.priority === 'high' || notification.priority === 'urgent';

  // cadence.* kinds are Mag just TELLING the founder something (recaps, briefs,
  // digests, insights) — not an ask. For those the footer is a single "got it"
  // (acknowledge + close; the drawer already marked it read on open), instead
  // of a "review it" action that implies there's something to act on.
  const isInformational = notification.kind.startsWith('cadence.');

  return (
    <Drawer
      visible={visible}
      onRequestClose={requestClose}
      maxWidthClass="max-w-[480px]"
      ariaLabelledBy="notification-drawer-title"
    >
      <header className="flex items-start justify-between gap-3 border-b border-[var(--line)] bg-white px-5 py-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <BrandChip brand={brand} fallbackName={notification.brandId ? '…' : 'magig'} />
            <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
              {notification.kind} · {relativeTime(created)}
            </span>
            {priorityBadge ? (
              <span className="rounded-full bg-[var(--pulse)]/10 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--pulse)]">
                {notification.priority}
              </span>
            ) : null}
          </div>
          <h2
            id="notification-drawer-title"
            className="mt-2 font-display text-[20px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--ink)]"
          >
            {notification.title}
          </h2>
        </div>
        <DrawerCloseButton onClick={requestClose} />
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        <div className="flex flex-col gap-4">
          <p className="mag-voice whitespace-pre-wrap text-[15px] leading-[1.55] text-[var(--ink)]/85">
            {notification.body}
          </p>

          {/* "What mag's asking" — the explicit approval ask. Without
              this an ask.approval drawer reads like a plain heads-up;
              the founder sees the body but not what they're being
              asked to sign off on. Plum-tinted to mark it as mag's
              request, distinct from the body. Mirrors FeedCardAsk. */}
          {meta.action ? (
            <div className="rounded-xl border border-[var(--plum)]/30 bg-[var(--plum)]/[0.04] p-4">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                {'↘'} <Trans>mag needs your call</Trans>
              </p>
              <p className="mt-2 text-[14px] leading-[1.5] text-[var(--ink)]/85">{meta.action}</p>
              {destination ? (
                <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                  <Trans>open it to decide ↗</Trans>
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <footer className="flex items-center justify-end gap-3 border-t border-[var(--line)] bg-white px-5 py-4">
        {destination && !isInformational ? (
          <>
            <button type="button" onClick={requestClose} className="btn btn-ghost">
              <Trans>close</Trans>
            </button>
            <button type="button" onClick={handleCta} className="btn btn-spell">
              {destination.label}
              <span className="btn-arrow">→</span>
            </button>
          </>
        ) : (
          <button type="button" onClick={requestClose} className="btn btn-spell">
            <Trans>got it</Trans>
            <span className="btn-arrow">→</span>
          </button>
        )}
      </footer>
    </Drawer>
  );
}

/** Minimal slide-in shell shown while the router resolves whether a
 *  notification is backed by a slot card (cold cross-brand open only).
 *  Same chrome as the drawers it stands in for so the swap doesn't
 *  jump. */
function NotificationDrawerLoading({ onClose }: { onClose: () => void }) {
  const { t } = useLingui();
  const { visible, requestClose } = useDrawer(onClose);
  return (
    <Drawer
      visible={visible}
      onRequestClose={requestClose}
      maxWidthClass="max-w-[480px]"
      ariaLabel={t`loading notification`}
      panelClassName="items-center justify-center"
    >
      <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
        {'↘'} <Trans>loading…</Trans>
      </p>
    </Drawer>
  );
}

/** Pull the actionable bits out of a notification's payload. payload is
 *  typed `unknown` (the discriminated union lives in @magig/schemas);
 *  we read defensively here rather than importing the full zod parse —
 *  the drawer only needs two fields and must never throw on an
 *  unexpected shape.
 *
 *  - feedCardId: present on most payloads via basePayloadShape, even
 *    when the top-level column is null.
 *  - action: ask.approval's one-phrase description of what's up for
 *    approval. */
function readPayloadMeta(notification: NotificationItem): {
  feedCardId: string | null;
  action: string | null;
} {
  const p = notification.payload;
  if (!p || typeof p !== 'object') return { feedCardId: null, action: null };
  const obj = p as Record<string, unknown>;
  const feedCardId = typeof obj['feedCardId'] === 'string' ? obj['feedCardId'] : null;
  const action =
    notification.kind === 'ask.approval' && typeof obj['action'] === 'string'
      ? obj['action']
      : null;
  return { feedCardId, action };
}

/** Inject ?card=<feedCardId> into a relative href when the notification
 *  is backed by a feed card, so the landing surface opens the
 *  FeedCardDrawer. Preserves any existing query string; absolute urls
 *  and hrefs that already carry ?card= pass through untouched. Mirrors
 *  the helper that used to live in notifications-bell.tsx. */
function withFeedCardParam(href: string, feedCardId: string | null): string {
  if (!feedCardId) return href;
  if (!href.startsWith('/')) return href;
  if (/[?&]card=/.test(href)) return href;
  const sep = href.includes('?') ? '&' : '?';
  return `${href}${sep}card=${encodeURIComponent(feedCardId)}`;
}

function relativeTime(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return formatDate(d);
}
